import { useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';

import { adaptPageURLToProductsQueryActiveState } from 'src/adapters/pageURLToStore/adaptPageURLToProductsQueryActiveState';
import { adaptPageURLToStore } from 'src/adapters/pageURLToStore/adaptPageURLToStore';
import { adaptStoreToPageURL } from 'src/adapters/storeToPageURL/adaptStoreToPageURL';
import { serializeURLSearchParamsStably } from 'src/adapters/storeToPageURL/serializeURLSearchParamsStably';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { type RequestProductsMetadata, TriggerName } from 'src/redux/products/types';
import { type SyncProductsQueryFromURLPayload, actions } from 'src/redux/productsQuery/slice';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { AppThunkDispatch } from 'src/redux/store';
import { PageType } from 'src/types/navigation';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';
import { consumePendingProductsQueryURLSyncIntent } from './productsQueryURLSyncIntent';

type ProductsQueryURLSyncState = {
  navigation: Pick<ReduxApplicationState['navigation'], 'pageType'>;
  productsQuery: ReduxApplicationState['productsQuery'];
};

const serializeProductsQuery = (
  state: ProductsQueryURLSyncState,
  productsQuery: ReduxApplicationState['productsQuery'],
): string =>
  serializeURLSearchParamsStably(
    adaptStoreToPageURL(
      {
        ...state,
        productsQuery,
      },
      productsQuery.page,
    ),
  );

const applyURLActiveStateToProductsQuery = (
  productsQuery: ReduxApplicationState['productsQuery'],
  active: SyncProductsQueryFromURLPayload,
): ReduxApplicationState['productsQuery'] => ({
  ...productsQuery,
  facets: productsQuery.facets.map((facet) => {
    const activeValues = new Set(active.activeFacetValuesByKey[facet.key] ?? []);

    return {
      ...facet,
      options: facet.options.map((option) => ({
        ...option,
        active: activeValues.has(option.value),
      })),
    };
  }),
  sortOptions: productsQuery.sortOptions.map((option) => ({
    ...option,
    active:
      active.activeSort != null &&
      option.key === active.activeSort.key &&
      option.descending === active.activeSort.descending,
  })),
  page: active.page,
  searchTerm: active.searchTerm,
});

export const getFacetChangeMetadata = (
  currentFacets: Facet[],
  nextActiveFacetValuesByKey: SyncProductsQueryFromURLPayload['activeFacetValuesByKey'],
): RequestProductsMetadata | undefined => {
  const changedOptions = currentFacets.flatMap((currentFacet) => {
    const nextActiveValues = new Set(nextActiveFacetValuesByKey[currentFacet.key] ?? []);

    return currentFacet.options.flatMap((currentOption) => {
      const nextActive = nextActiveValues.has(currentOption.value);

      if (currentOption.active === nextActive) {
        return [];
      }

      return [
        {
          facetTitle: currentFacet.title,
          optionActive: nextActive,
          optionTitle: currentOption.title,
        },
      ];
    });
  });

  return changedOptions.length === 1 ? changedOptions[0] : undefined;
};

export const getProductsQueryStateFromURL = (
  query: ParsedUrlQuery,
  state: ProductsQueryURLSyncState,
): SyncProductsQueryFromURLPayload =>
  adaptPageURLToProductsQueryActiveState(adaptPageURLToStore(query), {
    includeSearchTerm: state.navigation.pageType === PageType.Search,
  });

export const productsQueryMatchesURL = (
  query: ParsedUrlQuery,
  state: ProductsQueryURLSyncState,
): boolean => {
  const nextActiveState = getProductsQueryStateFromURL(query, state);
  const current = serializeProductsQuery(state, state.productsQuery);
  const next = serializeProductsQuery(
    state,
    applyURLActiveStateToProductsQuery(state.productsQuery, nextActiveState),
  );

  return current === next;
};

export const getRouteChangeTriggerName = (
  state: ProductsQueryURLSyncState,
  nextActiveState: SyncProductsQueryFromURLPayload,
): TriggerName => {
  const nextProductsQuery = applyURLActiveStateToProductsQuery(
    state.productsQuery,
    nextActiveState,
  );
  const current = adaptStoreToPageURL(state, state.productsQuery.page);
  const next = adaptStoreToPageURL(
    {
      ...state,
      productsQuery: nextProductsQuery,
    },
    nextProductsQuery.page,
  );
  const currentSort = current.get('sort');
  const nextSort = next.get('sort');
  const currentPage = current.get('page');
  const nextPage = next.get('page');

  current.delete('page');
  next.delete('page');

  if (
    currentPage !== nextPage &&
    serializeURLSearchParamsStably(current) === serializeURLSearchParamsStably(next)
  ) {
    return TriggerName.PAGE_NUMBER_CHANGE;
  }

  current.delete('sort');
  next.delete('sort');

  if (
    currentSort !== nextSort &&
    serializeURLSearchParamsStably(current) === serializeURLSearchParamsStably(next)
  ) {
    return TriggerName.CHANGE_SORT;
  }

  return TriggerName.CHANGE_FACETS;
};

export const useSyncProductsQueryFromUrl = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const router = useRouter();
  const lastHandledAsPath = useRef<string | null>(null);
  const pageType = useSelector((state: ReduxApplicationState) => state.navigation.pageType);
  const productsQuery = useSelector((state: ReduxApplicationState) => state.productsQuery);

  useEffect(() => {
    const urlSyncState: ProductsQueryURLSyncState = {
      navigation: {
        pageType,
      },
      productsQuery,
    };

    if (!router.isReady) {
      return;
    }

    if (lastHandledAsPath.current === router.asPath) {
      return;
    }

    lastHandledAsPath.current = router.asPath;

    if (productsQueryMatchesURL(router.query, urlSyncState)) {
      return;
    }

    const nextActiveState = getProductsQueryStateFromURL(router.query, urlSyncState);

    batch(() => {
      const urlSyncIntent = consumePendingProductsQueryURLSyncIntent(router.asPath);
      const triggerName =
        urlSyncIntent?.triggerName ?? getRouteChangeTriggerName(urlSyncState, nextActiveState);
      const metadata =
        urlSyncIntent?.metadata ??
        (triggerName === TriggerName.CHANGE_FACETS
          ? getFacetChangeMetadata(productsQuery.facets, nextActiveState.activeFacetValuesByKey)
          : undefined);

      dispatch(actions.syncFromURL(nextActiveState));
      dispatch(
        requestProducts({
          metadata,
          pageLoading: nextActiveState.page,
          triggerName,
        }),
      );
    });

    scrollToTopOfProductsList();
  }, [dispatch, pageType, productsQuery, router.asPath, router.isReady, router.query]);
};
